import {alias, createModelSchema, list, reference} from "serializr";

import {TriggerBase} from "./core/TriggerBase";
import {TriggerKind} from "./core/TriggerKind";
import {TriggerScope} from "./core/TriggerScope";

import {ItemKey} from "../item/core/ItemKey";
import {ItemGroupModel} from "../item/core/ItemGroupModel";
import {Schema} from "@/common/Schema";
import {Tile} from "@/modules/redactor/models/utils/Tile";

export class TriggerItem extends TriggerBase {

    static MERGE_COUNTING_TYPES = [
        { text: 'DEFAULT', value: 0 },
        { text: 'ITEMS_RECEIVED', value: 1 },
        { text: 'ITEMS_MERGED', value: 2 }
    ];

    constructor() {
        super(TriggerKind.ITEM);
        this._item = null;
        this._length = 0;
        this._tiles = [];
        this._merge_counting_type = 0;
    }

    get item() {
        return this._item;
    }

    set item(value) {
        this._item = value;
    }

    get length() {
        return this._length;
    }

    set length(value) {
        this._length = value;
    }

    get tiles() {
        return this._tiles;
    }

    set tiles(value) {
        this._tiles = value;
    }

    get mergeCountingType() {
        return this._merge_counting_type;
    }

    set mergeCountingType(value) {
        this._merge_counting_type = value;
    }

    valid() {
        if (!super.valid()) {
            return false;
        }

        if (!Number.isInteger(this.length) || this.length < 0) {
            return false;
        }

        return this.item && this.item.valid();
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new TriggerItem());
        } else if (target instanceof TriggerItem) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

export class TriggerItemGroup extends TriggerBase {

    constructor() {
        super(TriggerKind.ITEM_GROUP);
        this._group = ItemGroupModel.isNone("none");
        this._length = 0;
    }

    get group() {
        return this._group;
    }

    set group(value) {
        this._group = value;
    }

    get length() {
        return this._length;
    }

    set length(value) {
        this._length = value;
    }

    valid() {
        return super.valid() && this.group;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new TriggerItemGroup());
        } else if (target instanceof TriggerItemGroup) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

export class TriggerItemScope extends TriggerScope {
    static _values = [
        new TriggerItemScope(6, "JOB_BUILD_START"),
        new TriggerItemScope(1, "JOB_BUILD_COMPLETE"),
        new TriggerItemScope(7, "JOB_MINE_START"),
        new TriggerItemScope(2, "JOB_MINE_COMPLETE"),
        new TriggerItemScope(8, "JOB_HARVEST_START"),
        new TriggerItemScope(3, "JOB_HARVEST_COMPLETE"),
        new TriggerItemScope(9, "JOB_REMOVE_START"),
        new TriggerItemScope(4, "JOB_REMOVE_COMPLETE"),
        new TriggerItemScope(10, "JOB_READY_START"),
        new TriggerItemScope(5, "JOB_READY_COMPLETE"),

        new TriggerItemScope(15, "UNLOCK_LEVEL"),
        new TriggerItemScope(16, "UNLOCK_MANA"),
        new TriggerItemScope(17, "UNLOCK_PATH"),

        new TriggerItemScope(20, "CREATE"),
        new TriggerItemScope(21, "AVAILABLE"),
        new TriggerItemScope(23, "REMOVE"),
        new TriggerItemScope(24, "MERGE"),
        new TriggerItemScope(49, "DISCOVERY"),
        new TriggerItemScope(50, "DISCOVERY_NOW"),

        new TriggerItemScope(26, "REWARD_AVAILABLE"),
        new TriggerItemScope(22, "COLLECT_ITEM"),
        new TriggerItemScope(54, "TOUCH_ITEM"),

        new TriggerItemScope(25, "COLLECT_REWARD"),
        new TriggerItemScope(42, "COLLECT_REWARD_BUILD"),
        new TriggerItemScope(43, "COLLECT_REWARD_MINE"),
        new TriggerItemScope(44, "COLLECT_REWARD_HARVEST"),
        new TriggerItemScope(45, "COLLECT_REWARD_REMOVE"),
        new TriggerItemScope(46, "COLLECT_REWARD_READY"),

        new TriggerItemScope(51, "EXIST"),
        new TriggerItemScope(52, "EXIST_IDLE"),
        new TriggerItemScope(53, "EXIST_RUNNING"),

        new TriggerItemScope(37, "EXIST_BUILD"),
        new TriggerItemScope(27, "EXIST_BUILD_IDLE"),
        new TriggerItemScope(28, "EXIST_BUILD_RUNNING"),
        new TriggerItemScope(38, "EXIST_MINE"),
        new TriggerItemScope(29, "EXIST_MINE_IDLE"),
        new TriggerItemScope(30, "EXIST_MINE_RUNNING"),
        new TriggerItemScope(39, "EXIST_HARVEST"),
        new TriggerItemScope(31, "EXIST_HARVEST_IDLE"),
        new TriggerItemScope(32, "EXIST_HARVEST_RUNNING"),
        new TriggerItemScope(40, "EXIST_REMOVE"),
        new TriggerItemScope(33, "EXIST_REMOVE_IDLE"),
        new TriggerItemScope(34, "EXIST_REMOVE_RUNNING"),
        new TriggerItemScope(41, "EXIST_READY"),
        new TriggerItemScope(35, "EXIST_READY_IDLE"),
        new TriggerItemScope(36, "EXIST_READY_RUNNING"),

        new TriggerItemScope(47, "ORDER_DELIVERY"),
        new TriggerItemScope(48, "ORDER_DELETE"),
        
        new TriggerItemScope(55, "POTENTIAL_MERGE")
    ];

    static getValues() {
        return this._values;
    }

    static getValue(id) {
        let value = this._values.find(v => v.id === id) || null;
        console.assert(value, "unknown id: " + id);
        return value;
    }

    static schema() {
        return reference("id", (id, cb) => {
            return cb(null, TriggerItemScope.getValue(id));
        });
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new TriggerItemScope());
        } else if (target instanceof TriggerItemScope) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(TriggerItem, {
    _scope: alias("scope", TriggerItemScope.schema()),
    _item: alias("item", ItemKey.schema()),
    _length: alias("length", Schema.primitive()),
    _tiles: alias("tiles", list(Tile.schema())),
    _merge_counting_type: alias("merge_counting_type", Schema.primitive()),
});

createModelSchema(TriggerItemGroup, {
    _scope: alias("scope", TriggerItemScope.schema()),
    _group: alias("group", ItemGroupModel.schemaByCode()),
    _length: alias("length", Schema.primitive())
});
